import { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { alpha } from '@mui/material/styles';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import apiReq from 'core/api';
import type { Project } from 'types/Project';
import type { User } from 'types/User';
import type { UserActionHttpMethod, UserActionsFilters } from '../types';
import { dateInputToUnixSec, unixSecToDateInput } from '../utils';
import { useUserActionCategories } from '../hooks/use-categories';

const HTTP_METHODS: UserActionHttpMethod[] = ['POST', 'PATCH', 'DELETE'];

interface Props {
  filters: UserActionsFilters;
  onChange: (updater: (prev: UserActionsFilters) => UserActionsFilters) => void;
  onReset: () => void;
}

export function FiltersBar({ filters, onChange, onReset }: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const { categories, actions } = useUserActionCategories(filters.category);

  useEffect(() => {
    apiReq<Project[]>('projects')
      .then((d) => setProjects(d ?? []))
      .catch(() => setProjects([]));
    apiReq<User[]>('user/all')
      .then((d) => setUsers(d ?? []))
      .catch(() => setUsers([]));
  }, []);

  const selectedUser = users.find((u) => u.id === filters.user_id) ?? null;
  const selectedProject = projects.find((p) => p.id === filters.project_id) ?? null;

  return (
    <Stack
      spacing={1.5}
      sx={{
        p: 1.5,
        background: (t) => t.palette.background.paper,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap>
        <TextField
          label="С даты"
          type="date"
          size="small"
          value={unixSecToDateInput(filters.date_from)}
          onChange={(e) =>
            onChange((prev) => ({ ...prev, date_from: dateInputToUnixSec(e.target.value, false) }))
          }
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="По дату"
          type="date"
          size="small"
          value={unixSecToDateInput(filters.date_to)}
          onChange={(e) =>
            onChange((prev) => ({ ...prev, date_to: dateInputToUnixSec(e.target.value, true) }))
          }
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <Autocomplete
          size="small"
          options={users}
          getOptionLabel={(o) => o.name || o.email}
          getOptionKey={(o) => o.id ?? o.email}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={selectedUser}
          onChange={(_, v) => onChange((prev) => ({ ...prev, user_id: v?.id ?? null }))}
          renderInput={(params) => <TextField {...params} label="Пользователь" />}
          sx={{ minWidth: 220 }}
        />
        <Autocomplete
          size="small"
          options={projects}
          getOptionLabel={(o) => o.name}
          getOptionKey={(o) => o.id ?? o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={selectedProject}
          onChange={(_, v) => onChange((prev) => ({ ...prev, project_id: v?.id ?? null }))}
          renderInput={(params) => <TextField {...params} label="Проект" />}
          sx={{ minWidth: 200 }}
        />
        <Autocomplete
          size="small"
          options={categories}
          value={filters.category}
          onChange={(_, v) => onChange((prev) => ({ ...prev, category: v, action: null }))}
          renderInput={(params) => <TextField {...params} label="Категория" />}
          sx={{ minWidth: 180 }}
        />
        <Autocomplete
          size="small"
          options={actions}
          value={filters.action}
          onChange={(_, v) => onChange((prev) => ({ ...prev, action: v }))}
          renderInput={(params) => <TextField {...params} label="Действие" />}
          sx={{ minWidth: 180 }}
        />
        <TextField
          select
          size="small"
          label="Метод"
          value={filters.http_method ?? ''}
          onChange={(e) =>
            onChange((prev) => ({
              ...prev,
              http_method: (e.target.value || null) as UserActionHttpMethod | null,
            }))
          }
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Любой</MenuItem>
          {HTTP_METHODS.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          size="small"
          label="Поиск по URL и payload"
          value={filters.search}
          onChange={(e) => onChange((prev) => ({ ...prev, search: e.target.value }))}
          fullWidth
        />
        {(filters.subject_type || filters.subject_id) && (
          <Box
            sx={{
              fontSize: 12,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              background: (t) => alpha(t.palette.primary.main, 0.08),
              border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
              whiteSpace: 'nowrap',
            }}
          >
            Объект: {filters.subject_type ?? '—'}:{filters.subject_id ?? '—'}
            <Button
              size="small"
              onClick={() =>
                onChange((prev) => ({ ...prev, subject_type: null, subject_id: null }))
              }
              sx={{ ml: 1, minWidth: 'auto', p: 0.25 }}
            >
              ×
            </Button>
          </Box>
        )}
        <Button
          variant="outlined"
          onClick={onReset}
          startIcon={<RestartAltIcon />}
          sx={{ flexShrink: 0 }}
        >
          Сбросить
        </Button>
      </Stack>
    </Stack>
  );
}
